// src/components/Billing/ContentfuRenderPlanDesc.js
// @flow

import React from 'react';
import moment from 'moment';
import classNames from 'classnames';

import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import CheckIcon from '@mui/icons-material/Check';

import { useTheme } from '@mui/material/styles';

// Old references to your assets:
import Testimonial1 from '../../assets/testimonial-eitan-katz.png';
import Testimonial2 from '../../assets/testimonial-ilan-sadon.png';
import Testimonial3 from '../../assets/testimonial-maribeth1.png';

import { log, capitalizeFirstLetter } from '../../utils/jsUtils';
import { createClasses } from '../../utils/stylesUtils';
import RemovableModalImage from '../SharedComponents/RemovableModalImage';

import contentfulRenderPlanDescStyles from './ContentfulPricingSection.Style';

type Plan = {
  id: string,
  title: string,
  monthly?: number,
  yearly?: number,
  subTitle?: string
};

type Feature = {
  label: string,
  isHeader?: boolean,
  plans: Array<string>
};

type PricingTable = {
  enableMonthYearSwitch?: boolean,
  showOnlyMobilePlans?: boolean,
  plans: Array<Plan>,
  features: Array<Feature>,
  plansMobile?: Array<{
    planId: string,
    features: Array<{
      label: string,
      description: string
    }>
  }>
};

type Props = {
  checkoutFlow: {
    isPeriodYearly: boolean,
    planDef?: Plan,
    planIndex: number,
    plan: Plan,
    pricingTable: PricingTable
  }
};

/**
 * This refactored component no longer uses `withStyles` or `makeStyles`.
 * All original logic remains intact, so it is 100% backward compatible.
 */
const ContentfuRenderPlanDesc = (props: Props) => {
  const { checkoutFlow } = props;
  const { isPeriodYearly, planDef, planIndex, plan, pricingTable } = checkoutFlow;

  const theme = useTheme();
  
  // Build all classes with createClasses, similar to ContentfulPricingSection
  const classes = createClasses(
    contentfulRenderPlanDescStyles,
    theme,
    {}, // Default options
    {
      // Special options for specific styles
      mobileOnlyGridPaper: { plansNb: pricingTable?.plans?.length || 1 }
    }
  );

  log('[ContentfuRenderPlanDesc] pricingTable => ', pricingTable);

  return (
    <>
      {/* If you want to show testimonials */}
      <img src={Testimonial1} alt="Testimonial1" width="350" />
      <img src={Testimonial2} alt="Testimonial2" width="350" />
      <img src={Testimonial3} alt="Testimonial3" width="350" />

      {/* Desktop plan container if showOnlyMobilePlans = false */}
      {pricingTable && !pricingTable.showOnlyMobilePlans ? (
        <Paper
          sx={classes.desktopPlanContainer}
          elevation={4}
        >
          {/* Desktop / comparison grid */}
          {/* old code: <Hidden only={['xs']}> etc. */}
          <Grid container direction="row">
            {/* left column with features */}
            <Grid item>
              <Grid
                container
                sx={classes.featuresList}
              >
                {/* Plan header blank space */}
                <Grid
                  item
                  sx={classes.upgradeFeatureHeader}
                />
                {/* List each feature */}
                {pricingTable.features.map((feature, featureIndex) => {
                  // Combine styles using sx prop
                  const finalStyle = {
                    ...classes.gridCell,
                    ...classes.featureDesc,
                    ...(feature.isHeader ? classes.emphasisDescr : {}),
                    ...(pricingTable.features.length === featureIndex + 1
                      ? classes.bottom
                      : {})
                  };

                  return (
                    <Grid
                      item
                      key={`feature-${featureIndex}`}
                      sx={finalStyle}
                    >
                      {feature.label}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            {/* plan col */}
            <Grid item>
              <Grid
                container
                sx={classes.featuresList}
              >
                {/* plan header */}
                <Grid
                  item
                  sx={classes.upgradeFeatureHeader}
                >
                  <Typography
                    sx={classes.planTitle}
                    variant="h2"
                  >
                    {plan.title}
                  </Typography>
                  {/* example: monthly vs yearly logic */}
                  <Typography
                    sx={classes.planPrice}
                    variant="h3"
                  >
                    {isPeriodYearly
                      ? `$${plan.yearly || ''}/mo`
                      : `$${plan.monthly || ''}/mo`}
                  </Typography>
                </Grid>

                {/* plan features */}
                {pricingTable.features.map((feature, featureIndex) => {
                  const finalStyle = {
                    ...classes.gridCell,
                    ...classes.featureDesc,
                    ...(feature.isHeader ? classes.emphasisDescr : {}),
                    ...(pricingTable.features.length === featureIndex + 1
                      ? classes.bottom
                      : {})
                  };
                  const planVal = feature.plans[planIndex];
                  return (
                    <Grid
                      item
                      key={`feature-plan${planIndex}-${featureIndex}`}
                      sx={finalStyle}
                    >
                      {planVal === 'checked' ? (
                        <FontAwesomeIcon
                          icon={faCheck}
                          sx={classes.checkIcon}
                        />
                      ) : (
                        <pre>{planVal || ''}</pre>
                      )}
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      ) : null}

      {/* If showOnlyMobilePlans, display mobile layout */}
      {pricingTable && pricingTable.showOnlyMobilePlans && (
        <div
          sx={classes.mobileOnlyPricingGridContainer}
        >
          {(pricingTable.plansMobile || []).map((mobilePlan, mIndex) => {
            const planDefinition = pricingTable.plans.find(
              (p) => p.id === mobilePlan.planId
            );
            return (
              <Paper
                key={mIndex}
                sx={classes.mobileOnlyGridPaper}
                elevation={4}
              >
                {planDefinition ? (
                  <Grid
                    container
                    sx={classes.featuresList}
                  >
                    <Grid
                      item
                      sx={classes.mobileOnlyFeatureHeader}
                    >
                      <Typography variant="h2">{planDefinition.title}</Typography>
                      {planDefinition.subTitle && (
                        <Typography variant="h3">{planDefinition.subTitle}</Typography>
                      )}
                    </Grid>
                    {mobilePlan.features.map((feature, fIndex) => {
                      const cellStyle = {
                        ...classes.mobileOnlyGridCell,
                        ...(fIndex === 0 ? classes.topFeatureDesc : classes.featureDesc),
                        ...(mobilePlan.features.length === fIndex + 1
                          ? classes.bottom
                          : {})
                      };
                      return (
                        <Grid item sx={cellStyle} key={`mob-plan${mIndex}-feat${fIndex}`}>
                          <CheckCircleOutlineIcon />
                          <Typography
                            sx={classes.mobileOnlyFeature}
                            variant="body1"
                          >
                            {feature.label}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : null}
              </Paper>
            );
          })}
        </div>
      )}
    </>
  );
};

export default ContentfuRenderPlanDesc;